import { memo, useEffect, useMemo } from 'react';
import {
  useChatStoreExtract,
  useIsStepConditionallyVisible,
  useIsStepVisible,
  useMostRecentResponse,
  useStepParams,
  useStepResponse,
} from '../Chat/hooks/ChatStore.hooks';
import { MultiTurnThreadHistory } from './ThreadHistory';
import { getStepDescriptorByType } from './definitions';
import { ChatStoreState } from '../Chat/hooks/types';
import { memoize } from 'proxy-memoize';
import { isNil } from 'lodash';
import { getNullContextForStepType } from '@scale/shared/chat/api';
import { ContextType, InteractionStepResponse } from '@scale/shared/types/chat';

interface StepPrefetchProps {
  responseIndex: number;
}
function StepPrefetch({ responseIndex }: StepPrefetchProps) {
  const config = useChatStoreExtract(state => state.config);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const responses = useChatStoreExtract(state => state.responses);
  const response = useStepResponse(responseIndex, true);
  const stepParams = useStepParams(responseIndex);
  const isStepVisible = useIsStepVisible(responseIndex);
  const isStepConditionallyVisible = useIsStepConditionallyVisible(responseIndex);
  const completedSteps = useChatStoreExtract(state => state.completedSteps);

  const markStepAsCompletedByIndex = useChatStoreExtract(state => state.markStepAsCompletedByIndex);
  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );

  const memoizedIsCurrentStepSelector = memoize((state: ChatStoreState) => {
    const currentStepIndex = state.responses.length;
    return currentStepIndex === responseIndex;
  });
  const isCurrentStep = useChatStoreExtract(memoizedIsCurrentStepSelector);

  /*
   * We don't need to narrow down the responses here with responseIndex, because we need the actual last message
   * This is because we only use lastMessage in scenarios where it definitely represents the last message in the chat
   * (i.e. there are no future turns that exist with a lastMessage that would overwrite it)
   */
  const lastMessage = useMostRecentResponse();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const step = getStepDescriptorByType(stepParams.type);

  // TODO: Remove this once prefetching is moved to the backend
  const setPrefetchingInProgress = useChatStoreExtract(state => state.setPrefetchingInProgress);
  const setResponseForIndex = useChatStoreExtract(state => state.setResponseForIndex);

  const threadHistory = useMemo(
    () => new MultiTurnThreadHistory(config, responses, reviewLevel),
    [config, responses, reviewLevel],
  );

  useEffect(() => {
    if (!step || response) {
      return;
    }
    // TODO: @Lajos Prefetch is only on skip steps for now. Roll out more broadly if this ends up being stable.
    if (!isStepVisible && step.requiresSkipContext(stepParams)) {
      step.doPrefetch(stepParams, threadHistory);
    }
  }, [threadHistory, step, response, isStepVisible, stepParams]);

  useEffect(() => {
    async function getSkippedContext() {
      if (!isCurrentStep || !isNil(response) || !step) return;
      if (!isStepVisible || !isStepConditionallyVisible) {
        // NOTE: @Lajos This is the only place where we handle the null context.
        // Right now we are getting this from the shared library, but we should move this to the StepDescriptor.
        // It won't be a problem, since the shared chat validation will live in the chat package as well later!
        let context = getNullContextForStepType(stepParams.type);
        if (step.requiresSkipContext(stepParams)) {
          setPrefetchingInProgress(true);
          const prefetchContext = await step.loadSkipContext(stepParams, threadHistory); // TODO: @Lajos Remove threadHistory from here!
          setPrefetchingInProgress(false);
          context = {
            ...context,
            ...prefetchContext,
          };
        }
        setResponseForIndex(
          {
            type: stepParams.type,
            context,
            contextType: ContextType.Skipped,
            output: lastMessage,
            // forcing the cast to InteractionStepResponse to avoid type errors
            // because getFlattenedStepsWithResponses is using a tuple of StepParamsForTypeWithStage<InteractionStepType> and IndexedInteractionStepResponse, and there's a hard limit on the number of elements in a tuple
          } as unknown as InteractionStepResponse,
          responseIndex,
        );
      }
    }
    getSkippedContext();
  }, [
    responseIndex,
    step,
    stepParams,
    reviewLevel,
    lastMessage,
    threadHistory,
    setResponseForIndex,
    isCurrentStep,
    response,
    isStepConditionallyVisible,
    isStepVisible,
    setPrefetchingInProgress,
  ]);

  // We may need to mark skipped steps as complete / incomplete when their conditional visibility changes
  useEffect(() => {
    if (isNil(response) || !isStepVisible) return;
    // Skipped steps that are complete but become conditionally visible should be marked as incomplete
    if (
      response.contextType === ContextType.Skipped &&
      completedSteps[responseIndex] &&
      isStepConditionallyVisible
    ) {
      markStepAsIncompletedByIndex(responseIndex);
      return;
    }
    // Skipped steps that are incomplete but become conditionally invisible should be marked as complete
    if (
      response.contextType === ContextType.Skipped &&
      !completedSteps[responseIndex] &&
      !isStepConditionallyVisible
    ) {
      markStepAsCompletedByIndex(responseIndex);
      return;
    }
  }, [
    completedSteps,
    responseIndex,
    response,
    isStepVisible,
    isStepConditionallyVisible,
    markStepAsIncompletedByIndex,
    markStepAsCompletedByIndex,
  ]);
  return null;
}

export default memo(StepPrefetch);
